import { useEffect } from 'react';

/**
 * ======================================================
 * DATATABLE TOOLTIP/POPOVER SYSTEM
 * ======================================================
 *
 * Z-INDEX LAYERS (from highest to lowest):
 * ----------------------------------------
 * MODAL:       10000  - Full-screen modals with backdrop
 * POPOVER:      9999  - Click-activated detailed popovers (element results, JSON)
 * TOAST:         400  - Temporary feedback toasts (delete confirmation)
 * DROPDOWN:      300  - Form dropdowns, edit popovers (column edit)
 * TOOLTIP:       200  - Hover tooltips, truncated text hints
 * HEADER:        100  - Sticky table headers
 * FLOAT:          50  - Floating action buttons
 *
 * BEHAVIOR CONVENTIONS:
 * ---------------------
 * - Hover tooltips: opacity-0 -> group-hover:opacity-100, pointer-events-none
 * - Click popovers: useState toggle, useClickOutside hook, position: fixed
 * - Toasts: Auto-dismiss setTimeout, slide-in animation
 *
 * STYLING PATTERNS:
 * -----------------
 * - Dark mode: bg-zinc-800 text-gray-200 border-zinc-700
 * - Light mode: bg-white text-gray-800 border-gray-200
 * - Small text: text-[11px] or text-xs
 * - Padding: px-2.5 py-1.5 for tooltips, p-3 for popovers
 *
 * ADDING NEW POPOVERS:
 * --------------------
 * 1. Import { Z_INDEX, useClickOutside, getPopoverPosition } from './tooltip'
 * 2. Use appropriate Z_INDEX constant in className: `z-[${Z_INDEX.POPOVER}]`
 * 3. For click popovers: useClickOutside([buttonRef, popoverRef], close, isOpen)
 * 4. For positioning: const pos = getPopoverPosition(buttonRef.current.getBoundingClientRect())
 *
 * ======================================================
 */

// Z-index constants - use in Tailwind as z-[${Z_INDEX.TOOLTIP}]
export const Z_INDEX = {
  MODAL: 10000,
  POPOVER: 9999,
  TOAST: 400,
  DROPDOWN: 300,
  TOOLTIP: 200,
  HEADER: 100,
  FLOAT: 50,
};

// Tooltip/popover base styles
export const POPOVER_STYLES = {
  dark: 'bg-zinc-800 text-gray-200 border border-zinc-700 shadow-lg rounded-lg',
  light: 'bg-white text-gray-800 border border-gray-200 shadow-lg rounded-lg',
};

/**
 * Hook: Close popover when clicking outside
 * @param {RefObject[]} refs - Array of refs to exclude from "outside" clicks
 * @param {Function} onClose - Callback when clicking outside
 * @param {boolean} isOpen - Only listen when popover is open
 */
export function useClickOutside(refs, onClose, isOpen) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      const isOutside = refs.every(
        (ref) => ref.current && !ref.current.contains(e.target)
      );
      if (isOutside) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [refs, isOpen, onClose]);
}

/**
 * Calculate popover position to avoid viewport overflow
 * @param {DOMRect} buttonRect - getBoundingClientRect() of trigger button
 * @param {number} popoverWidth - Expected width of popover (default 400)
 * @returns {{ top: number, left: number }}
 */
export function getPopoverPosition(buttonRect, popoverWidth = 400) {
  return {
    top: buttonRect.bottom + 4,
    left: Math.min(buttonRect.left, window.innerWidth - popoverWidth - 16),
  };
}
